import { CmsGift, CmsOffer, GiftListItem } from '../../types/cmsProduct';
import { localeSpecificGroup } from '../override';
import { tr } from '../translations/tr';

export const calculateRewardValue = (props: {
  welcomeOffers: CmsOffer[];
  exclusiveOffers: CmsOffer[];
}): number => {
  const { exclusiveOffers, welcomeOffers } = props;

  return (
    calculateOffersValue(exclusiveOffers) + calculateOffersValue(welcomeOffers)
  );
};

/**
 * `deductTopUp: false` values a gift at its full price — the personal-loan
 * provider source, which costs the reader nothing to receive (CMS-1922 R3).
 */
type GiftValuation = { deductTopUp?: boolean };

export const calculateOffersValue = (
  offers: CmsOffer[] = [],
  valuation?: GiftValuation,
): number => {
  return offers.reduce((acc, offer) => {
    return acc + getMaxGiftPriceFromGiftList(offer.list_of_gifts, valuation);
  }, 0);
};

export const getMaxGiftPriceFromGiftList = (
  gifts: GiftListItem[] = [],
  valuation?: GiftValuation,
): number => {
  if (gifts.length === 0) return 0;

  const prices = gifts.map((g) => getEffectiveGiftPrice(g?.gift, valuation));
  return Math.max(0, ...prices);
};

/** A gift's value net of its top-up (unless `deductTopUp` is off); 0 without a price. */
export const getEffectiveGiftPrice = (
  gift?: CmsGift,
  { deductTopUp = true }: GiftValuation = {},
): number => {
  const price = gift?.gift_value?.price;
  const topup = gift?.gift_value?.topup_amount;

  if (typeof price !== 'number' || price <= 0) return 0;

  const topupAmount =
    deductTopUp && typeof topup === 'number' && topup > 0 ? topup : 0;
  return Math.max(0, price - topupAmount);
};

const MS_IN_DAY = 1000 * 60 * 60 * 24;

/**
 * Whole days until `toDateStr`, rounded up, so under a day left reads 1 and a passed date 0 or less.
 * The one count every urgency signal shares, so a card and the PDP never disagree about an offer.
 */
export const getDaysUntil = (
  toDateStr: string,
  now: number,
): number | undefined => {
  const end = new Date(toDateStr).getTime();
  return Number.isNaN(end) ? undefined : Math.ceil((end - now) / MS_IN_DAY);
};

export type UrgencyPhase = {
  code: 'limitedTime' | '7to5' | '4to2' | 'endsToday';
  value: string;
};

export function getUrgencyPhase(
  lang: string,
  fromDateStr?: string,
  toDateStr?: string,
): UrgencyPhase | undefined {
  const fromDate = fromDateStr ? new Date(fromDateStr) : undefined;
  const toDate = toDateStr ? new Date(toDateStr) : undefined;

  if (
    !fromDate ||
    !toDate ||
    isNaN(fromDate.getTime()) ||
    isNaN(toDate.getTime())
  ) {
    return undefined;
  }

  const staticText = TRANSLATIONS.get(lang);

  // Both dates are validated above, so the shared count is a number here.
  const diffInDays = getDaysUntil(toDateStr!, Date.now())!;

  if (diffInDays < 1) {
    return {
      code: 'endsToday',
      value: tr('end_today', staticText),
    };
  } else if (diffInDays <= 4) {
    return {
      code: '4to2',
      value: tr('left_less_four_day', staticText, {
        count: diffInDays.toString(),
      }),
    };
  } else if (diffInDays <= 7) {
    return {
      code: '7to5',
      value: tr('left_less_seven_day', staticText, {
        count: diffInDays.toString(),
      }),
    };
  } else {
    return {
      code: 'limitedTime',
      value: tr('limited_time', staticText),
    };
  }
}

const TRANSLATIONS = localeSpecificGroup({
  end_today: {
    _: `Offer ends today!`,
    zh: `優惠僅限今日！`,
  },
  left_less_four_day: {
    _: `Only :count days left!`,
    zh: `只剩下 :count 天！`,
  },
  left_less_seven_day: {
    _: `:count days left!`,
    zh: `剩下 :count 天！`,
  },
  limited_time: {
    _: `Limited time only`,
    zh: `限時優惠`,
  },
});

export function getCommonUrgencyPhase(
  offers: CmsOffer[],
): UrgencyPhase | undefined {
  if (!offers?.length) return undefined;

  return offers.reduce<UrgencyPhase | undefined>((commonPhase, offer) => {
    const { start_date, end_date } = offer.period || {};

    if (!start_date || !end_date) {
      return undefined;
    }

    const currentPhase = getUrgencyPhase(start_date, end_date);

    if (!commonPhase) {
      return currentPhase;
    }

    if (commonPhase.code !== currentPhase?.code) {
      return undefined;
    }

    return commonPhase;
  }, undefined);
}
